"use client";
import React, { useRef, useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function FreshDealsSlider({ children }: { children: React.ReactNode }) {
  const sliderRef  = useRef<HTMLDivElement>(null);
  const isJumping  = useRef(false);
  const [cols, setCols] = useState(6);

  const items   = React.Children.toArray(children);
  const count   = items.length;
  const tripled = [...items, ...items, ...items];

  /* ── detect cols based on container width ── */
  const updateCols = useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;
    setCols(el.clientWidth < 640 ? 2 : 6);
  }, []);

  useEffect(() => {
    updateCols();
    window.addEventListener("resize", updateCols);
    return () => window.removeEventListener("resize", updateCols);
  }, [updateCols]);

  /* ── jump to middle clone on mount ── */
  useEffect(() => {
    const el = sliderRef.current;
    if (!el || count === 0) return;
    requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth / 3;
    });
  }, [count]);

  /* ── infinite loop ── */
  const handleScroll = useCallback(() => {
    const el = sliderRef.current;
    if (!el || isJumping.current) return;
    const third = el.scrollWidth / 3;
    if (el.scrollLeft >= third * 2 - 1) {
      isJumping.current = true;
      el.scrollLeft -= third;
      setTimeout(() => { isJumping.current = false; }, 50);
    } else if (el.scrollLeft <= 1) {
      isJumping.current = true;
      el.scrollLeft += third;
      setTimeout(() => { isJumping.current = false; }, 50);
    }
  }, []);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  /* ── scroll 1 card at a time ── */
  const doScroll = (dir: "left" | "right") => {
    const el = sliderRef.current;
    if (!el) return;
    const gap = 12;
    const cardW = (el.clientWidth - gap * (cols - 1)) / cols;
    el.scrollBy({
      left: dir === "right" ? -(cardW + gap) : (cardW + gap),
      behavior: "smooth",
    });
  };

  const gap = 12;
  const cardStyle = {
    flexShrink: 0 as const,
    width:    `calc((100% - ${(cols - 1) * gap}px) / ${cols})`,
    minWidth: `calc((100% - ${(cols - 1) * gap}px) / ${cols})`,
  };

  return (
    <div className="w-full">
      <div className="relative">

        {/* LEFT */}
        <button
          onClick={() => doScroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 z-20
            w-9 h-9 rounded-xl bg-indigo-800 border border-white/10
            flex items-center justify-center text-slate-300
            hover:bg-blue-600 hover:border-blue-600 hover:text-white
            transition-all duration-200 active:scale-90 shadow-lg"
        >
          <ChevronLeft size={16} strokeWidth={2.5} />
        </button>

        {/* RIGHT */}
        <button
          onClick={() => doScroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 z-20
            w-9 h-9 rounded-xl bg-indigo-800 border border-white/10
            flex items-center justify-center text-slate-300
            hover:bg-blue-600 hover:border-blue-600 hover:text-white
            transition-all duration-200 active:scale-90 shadow-lg"
        >
          <ChevronRight size={16} strokeWidth={2.5} />
        </button>

        {/* Track */}
        <div style={{ overflowX: "hidden", overflowY: "visible" }}>
          <div
            ref={sliderRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto py-3"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none", gap: `${gap}px` }}
          >
            {tripled.map((child, i) => (
              <div key={i} style={cardStyle}>
                {child}
              </div>
            ))}
          </div>
        </div>

        {/* fades */}
        <div className="absolute top-0 left-0  w-8 h-full bg-gradient-to-r from-indigo-950 to-transparent pointer-events-none z-10 -translate-y-3" />
        <div className="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-indigo-950 to-transparent pointer-events-none z-10 -translate-y-3" />
      </div>

      <style dangerouslySetInnerHTML={{ __html: "div::-webkit-scrollbar{display:none}" }} />
    </div>
  );
}
